<template>
  <div class="min-h-screen bg-gradient-to-br from-psychology-calm via-white to-psychology-warm">
    <!-- 顶部导航 -->
    <nav class="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
      <div class="container mx-auto px-4 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <router-link 
              to="/" 
              class="inline-flex items-center gap-2 px-3 py-2 hover:bg-gray-100 rounded-md transition-colors text-gray-600 hover:text-gray-900"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                <path d="M15 21v-8a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"></path>
                <path d="M3 10a2 2 0 0 1 .709-1.528l7-5.999a2 2 0 0 1 2.582 0l7 5.999A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
              </svg>
            </router-link>
            <div class="flex items-center gap-2">
              <span class="text-2xl">💕</span>
              <span class="font-semibold text-psychology-primary">年上年下恋爱倾向测试</span>
            </div>
          </div>
          <button 
            v-if="canGoPrevious"
            @click="previousQuestion" 
            class="inline-flex items-center gap-2 px-3 py-2 border border-gray-300 bg-white hover:bg-gray-50 rounded-md transition-colors text-gray-600"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
              <path d="m12 19-7-7 7-7"></path>
              <path d="M19 12H5"></path>
            </svg>
            上一题
          </button>
        </div>

        <!-- 进度条 -->
        <div class="mt-4">
          <div class="flex justify-between items-center mb-2">
            <span class="text-sm text-gray-600">
              维度 {{ currentDimension + 1 }}/4 · {{ currentDimensionName }}
            </span>
            <span class="text-sm font-medium text-psychology-primary">
              题目 {{ currentQuestion + 1 }}/10 · 总进度 {{ totalAnswered }}/40
            </span>
          </div>
          <div class="relative w-full overflow-hidden rounded-full bg-gray-200 h-2">
            <div 
              class="h-full bg-gradient-to-r from-psychology-primary to-psychology-secondary transition-all"
              :style="{ width: progressPercent + '%' }"
            ></div>
          </div>
        </div>
      </div>
    </nav>

    <!-- 主要内容 -->
    <main class="container mx-auto px-4 py-8">
      <div class="max-w-3xl mx-auto">
        <!-- 题目卡片 -->
        <div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
          <!-- 题目头部 -->
          <div class="bg-gradient-to-r from-psychology-primary to-psychology-secondary px-4 sm:px-6 py-3 sm:py-4">
            <div class="flex items-center justify-between text-white">
              <span class="text-base sm:text-lg font-semibold">
                {{ currentDimensionName }} - 第 {{ currentQuestion + 1 }} 题
              </span>
              <span class="text-xs sm:text-sm opacity-90">还剩 {{ remainingQuestions }} 题</span>
            </div>
          </div>

          <!-- 题目内容 -->
          <div class="p-4 sm:p-6 md:p-8">
            <!-- 题目文字 -->
            <h2 class="text-lg sm:text-xl md:text-2xl font-medium text-gray-800 mb-4 sm:mb-6 md:mb-8 leading-relaxed">
              {{ currentQuestionText }}
            </h2>

            <!-- 5级评分选项 -->
            <RatingButton 
              :key="`dimension-${currentDimension}-question-${currentQuestion}`"
              v-model="selectedRating" 
              :disabled="isRatingDisabled" 
            />
          </div>

          <!-- 底部操作栏 -->
          <div class="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 border-t border-gray-100">
            <div class="flex items-center justify-between">
              <button 
                v-if="canGoPrevious"
                @click="previousQuestion"
                class="inline-flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-colors border border-gray-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="m12 19-7-7 7-7"></path>
                  <path d="M19 12H5"></path>
                </svg>
                上一题
              </button>
              <div v-else></div>

              <button 
                v-if="!isLastQuestion"
                @click="nextQuestion"
                :disabled="!selectedRating"
                :class="[
                  'inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all',
                  selectedRating
                    ? 'bg-gradient-to-r from-psychology-primary to-psychology-secondary text-white hover:shadow-lg'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                ]"
              >
                下一题
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M5 12h14"></path>
                  <path d="m12 5 7 7-7 7"></path>
                </svg>
              </button>
              <button 
                v-else
                @click="submitTest"
                :disabled="!selectedRating"
                :class="[
                  'inline-flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all',
                  selectedRating
                    ? 'bg-gradient-to-r from-psychology-primary to-psychology-secondary text-white hover:shadow-lg'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                ]"
              >
                查看结果
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- 提示信息 -->
        <div class="mt-6 text-center text-sm text-gray-500">
          <p>💡 请根据您的真实感受评分，答题进度会自动保存</p>
        </div>
      </div>
    </main>

    <!-- 回到顶部 -->
    <BackToTop />

    <!-- 测试工具(仅开发环境) -->
    <TestQuickFill
      :answers="answers"
      :current-dimension="currentDimension"
      :current-question-in-dimension="currentQuestion"
      @update-answers="handleQuickFillAnswers"
      @submit="handleQuickSubmit"
    />
  </div>
</template>

<script>
import BackToTop from '../components/BackToTop.vue'
import RatingButton from '../components/assessment/RatingButton.vue'
import TestQuickFill from '../components/TestQuickFill.vue'
import { QUESTION_SETS } from '@/data/aypQuestions'
import { 
  getSessionById, 
  updateSession, 
  saveSessionProgress,
  completeSession
} from '../utils/sessionStorage.js'
import { calculateFullResult } from '@/utils/aypCalculator'
import { useToast } from 'vue-toastification'

export default {
  name: 'QuestionnairePage',
  components: {
    BackToTop,
    RatingButton,
    TestQuickFill
  },
  data() {
    return {
      sessionId: '',
      session: null,
      testType: 'self',
      currentDimension: 0,
      currentQuestion: 0,
      answers: [],
      selectedRating: null,
      isSubmitting: false,
      toast: null,
      isRatingDisabled: false,  // 禁用评分按钮标志
      autoJumpTimer: null,       // 自动跳转定时器
      isInitializing: true       // 初始化标志，避免初始化时触发自动跳转
    }
  },
  computed: {
    questionSet() {
      return QUESTION_SETS[this.testType] || QUESTION_SETS.self
    },
    currentDimensionData() {
      return this.questionSet[this.currentDimension] || this.questionSet[0]
    },
    currentDimensionName() {
      return this.currentDimensionData.dimension
    },
    currentQuestionText() {
      return this.currentDimensionData.questions[this.currentQuestion] || ''
    },
    totalAnswered() {
      return this.answers.length
    },
    progressPercent() {
      return Math.round((this.totalAnswered / 40) * 100)
    },
    remainingQuestions() {
      return 40 - this.totalAnswered
    },
    canGoPrevious() {
      return this.totalAnswered > 0
    },
    isLastQuestion() {
      return this.currentDimension === 3 && this.currentQuestion === 9
    }
  },
  watch: {
    selectedRating(newVal, oldVal) {
      // 初始化阶段不触发自动跳转
      if (this.isInitializing) {
        this.isInitializing = false
        return
      }
      
      // 只有从 null 变为有效值时才触发（避免重复触发）
      if (oldVal === null && newVal !== null && !this.isRatingDisabled) {
        this.handleRatingSelected()
      }
    }
  },
  created() {
    // 初始化 toast
    this.toast = useToast()
    this.initializeSession()
  },
  beforeUnmount() {
    // 清除自动跳转定时器
    if (this.autoJumpTimer) {
      clearTimeout(this.autoJumpTimer)
      this.autoJumpTimer = null
    }
  },
  methods: {
    initializeSession() {
      // 获取sessionId
      this.sessionId = this.$route.query.sessionId
      
      if (!this.sessionId) {
        this.toast.error('会话ID缺失，请重新开始测试')
        this.$router.push('/assessment/select-type')
        return
      }

      // 加载会话数据
      this.session = getSessionById(this.sessionId)
      
      if (!this.session) {
        this.toast.error('会话不存在，请重新开始测试')
        this.$router.push('/assessment/select-type')
        return
      }

      // 恢复会话状态
      this.testType = this.session.testType || 'self'
      this.currentDimension = this.session.currentDimension || 0
      this.currentQuestion = this.session.currentQuestion || 0
      this.answers = this.session.answers || []

      // 如果当前题目已经有答案，加载它
      const existingAnswer = this.answers.find(
        a => a.dimension === this.currentDimension && a.question === this.currentQuestion
      )
      if (existingAnswer) {
        this.selectedRating = existingAnswer.rating
      }

      // 初始化完成后，标记初始化完成（延迟一下，确保 watch 能正确识别）
      this.$nextTick(() => {
        this.isInitializing = false
      })

      console.log('Session initialized:', {
        sessionId: this.sessionId,
        testType: this.testType,
        dimension: this.currentDimension,
        question: this.currentQuestion,
        totalAnswers: this.answers.length
      })
    },
    
    /**
     * 处理评分选择后的自动跳转
     */
    handleRatingSelected() {
      // 防止重复触发
      if (this.isRatingDisabled) return
      
      // 禁用评分按钮
      this.isRatingDisabled = true
      
      // 记录当前题目位置（双重检查）
      const currentDimension = this.currentDimension
      const currentQuestion = this.currentQuestion
      
      // 保存答案
      this.saveAnswer()
      
      // 延迟自动跳转
      this.autoJumpTimer = setTimeout(() => {
        // 双重检查：确保还在同一题目
        if (this.currentDimension === currentDimension && 
            this.currentQuestion === currentQuestion) {
          
          if (this.isLastQuestion) {
            // 最后一题，不自动提交，只重新启用评分按钮，等待用户手动点击"完成测试"
            this.isRatingDisabled = false
          } else {
            // 非最后一题，自动跳转
            this.nextQuestion()
          }
        } else {
          // 如果题目已经改变，只重新启用按钮
          this.isRatingDisabled = false
        }
      }, 200)
    },
    
    saveAnswer() {
      if (!this.selectedRating) return

      // 检查是否已存在该题的答案
      const existingIndex = this.answers.findIndex(
        a => a.dimension === this.currentDimension && a.question === this.currentQuestion
      )

      const answer = {
        dimension: this.currentDimension,
        question: this.currentQuestion,
        rating: this.selectedRating
      }

      if (existingIndex >= 0) {
        // 更新现有答案
        this.answers[existingIndex] = answer
      } else {
        // 添加新答案
        this.answers.push(answer)
      }

      // 保存到会话
      this.saveProgress()
    },

    saveProgress() {
      // 更新会话数据
      updateSession(this.sessionId, {
        currentDimension: this.currentDimension,
        currentQuestion: this.currentQuestion,
        answers: this.answers
      })

      // 保存进度
      saveSessionProgress(this.sessionId, {
        testType: this.testType,
        currentDimension: this.currentDimension,
        currentQuestion: this.currentQuestion,
        answers: this.answers
      })
    },

    nextQuestion() {
      if (!this.selectedRating) {
        this.toast.warning('请先选择评分')
        return
      }

      // 清除可能存在的定时器
      if (this.autoJumpTimer) {
        clearTimeout(this.autoJumpTimer)
        this.autoJumpTimer = null
      }
      
      // 重新启用评分按钮
      this.isRatingDisabled = false
      // 标记为初始化状态，避免加载已有答案时触发自动跳转
      this.isInitializing = true

      // 保存当前答案
      this.saveAnswer()

      // 移动到下一题
      if (this.currentQuestion < 9) {
        // 同一维度的下一题
        this.currentQuestion++
      } else if (this.currentDimension < 3) {
        // 下一个维度的第一题
        this.currentDimension++
        this.currentQuestion = 0
      }

      // 清除选择
      this.selectedRating = null

      // 检查新位置是否已有答案
      const existingAnswer = this.answers.find(
        a => a.dimension === this.currentDimension && a.question === this.currentQuestion
      )
      if (existingAnswer) {
        this.selectedRating = existingAnswer.rating
      }

      // 保存进度
      this.saveProgress()

      // 初始化完成后，标记初始化完成
      this.$nextTick(() => {
        this.isInitializing = false
      })

      // 滚动到顶部
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    previousQuestion() {
      if (!this.canGoPrevious) return

      // 清除可能存在的定时器
      if (this.autoJumpTimer) {
        clearTimeout(this.autoJumpTimer)
        this.autoJumpTimer = null
      }
      
      // 重新启用评分按钮
      this.isRatingDisabled = false
      // 标记为初始化状态，避免加载已有答案时触发自动跳转
      this.isInitializing = true

      // 保存当前答案(如果有)
      if (this.selectedRating) {
        this.saveAnswer()
      }

      // 移动到上一题
      if (this.currentQuestion > 0) {
        // 同一维度的上一题
        this.currentQuestion--
      } else if (this.currentDimension > 0) {
        // 上一个维度的最后一题
        this.currentDimension--
        this.currentQuestion = 9
      }

      // 加载上一题的答案
      const existingAnswer = this.answers.find(
        a => a.dimension === this.currentDimension && a.question === this.currentQuestion
      )
      this.selectedRating = existingAnswer ? existingAnswer.rating : null

      // 保存进度
      this.saveProgress()

      // 初始化完成后，标记初始化完成
      this.$nextTick(() => {
        this.isInitializing = false
      })

      // 滚动到顶部
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },

    async submitTest() {
      if (!this.selectedRating) {
        this.toast.warning('请先选择评分')
        return
      }

      if (this.isSubmitting) return
      this.isSubmitting = true

      // 清除可能存在的定时器
      if (this.autoJumpTimer) {
        clearTimeout(this.autoJumpTimer)
        this.autoJumpTimer = null
      }

      try {
        // 保存最后一题的答案
        this.saveAnswer()

        // 验证答案完整性
        if (this.answers.length !== 40) {
          this.toast.error(`答题不完整，当前已答 ${this.answers.length}/40 题`)
          this.isSubmitting = false
          return
        }

        // 计算结果
        const result = calculateFullResult(
          this.answers,
          this.session.userInfo || {},
          this.testType
        )

        console.log('Test completed, result:', result)

        // 完成会话
        completeSession(this.sessionId, result)

        // 显示成功提示
        // this.toast.success('测试完成！正在生成结果...')

        // 跳转到计算页面，传递 sessionId
        this.$router.push({
          path: '/assessment/calculating',
          query: {
            sessionId: this.sessionId
          }
        })
      } catch (error) {
        console.error('Submit test error:', error)
        this.toast.error('提交失败，请重试')
        this.isSubmitting = false
      }
    },

    /**
     * 处理快速填充答案(开发环境)
     */
    handleQuickFillAnswers(newAnswers) {
      // newAnswers是一个长度为40的数组,每个元素是1-5的评分
      this.answers = newAnswers.map((rating, index) => ({
        dimension: Math.floor(index / 10),
        question: index % 10,
        rating: rating
      }))

      // 更新当前选中的评分
      const currentIndex = this.currentDimension * 10 + this.currentQuestion
      this.selectedRating = newAnswers[currentIndex]

      // 保存进度
      this.saveProgress()
    },

    /**
     * 处理快速提交(开发环境)
     */
    async handleQuickSubmit() {
      if (this.answers.length !== 40) {
        this.toast.error(`答题不完整，当前已答 ${this.answers.length}/40 题`)
        return
      }

      if (this.isSubmitting) return
      this.isSubmitting = true

      try {
        // 计算结果
        const result = calculateFullResult(
          this.answers,
          this.session.userInfo || {},
          this.testType
        )

        console.log('Quick submit completed, result:', result)

        // 完成会话
        completeSession(this.sessionId, result)

        // 显示成功提示
        //this.toast.success('测试完成！正在生成结果...')

        // 跳转到计算页面，传递 sessionId
        this.$router.push({
          path: '/assessment/calculating',
          query: {
            sessionId: this.sessionId
          }
        })
      } catch (error) {
        console.error('Quick submit error:', error)
        this.toast.error('提交失败，请重试')
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
button:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

button:not(:disabled):hover {
  transform: translateY(-2px);
}

button:not(:disabled):active {
  transform: translateY(0);
}
</style>
